import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Grape, Users, MapPin, Award } from 'lucide-react';

interface AnimatedCounterProps {
    target: number;
    suffix?: string;
    inView: boolean;
}

const AnimatedCounter = ({ target, suffix = '', inView }: AnimatedCounterProps) => {
    const [count, setCount] = useState(0);
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (!inView || hasAnimated.current) return;
        hasAnimated.current = true;

        let start = 0;
        const end = target;
        const timer = setInterval(() => {
            start += Math.ceil(end / 40);
            if (start >= end) {
                setCount(end);
                clearInterval(timer);
            } else {
                setCount(start);
            }
        }, 40);

        return () => clearInterval(timer);
    }, [inView, target]);

    return <span className="tabular-nums">{count}{suffix}</span>;
};

const StatsStrip = () => {
    const { language } = useLanguage();
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    const stats = {
        PL: [
            { icon: Grape, value: 100, suffix: '+', label: 'Produktów' },
            { icon: Users, value: 500, suffix: '+', label: 'Klientów' },
            { icon: MapPin, value: 100, suffix: '%', label: 'Z Włoch' },
            { icon: Award, value: 100, suffix: '%', label: 'Gwarancji smaku' },
        ],
        EN: [
            { icon: Grape, value: 100, suffix: '+', label: 'Products' },
            { icon: Users, value: 500, suffix: '+', label: 'Customers' },
            { icon: MapPin, value: 100, suffix: '%', label: 'From Italy' },
            { icon: Award, value: 100, suffix: '%', label: 'Taste guarantee' },
        ],
    };

    const s = stats[language];

    return (
        <section ref={ref} className="relative overflow-hidden bg-primary py-10 md:py-14">
            {/* Subtle pattern */}
            <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
                }}
            />
            <div className="absolute -left-20 top-0 h-40 w-40 rounded-full bg-white/5 blur-[60px]" />
            <div className="absolute -right-20 bottom-0 h-40 w-40 rounded-full bg-white/5 blur-[60px]" />

            <div className="container relative z-10 mx-auto px-4 max-w-[1200px]">
                <div className="grid grid-cols-2 gap-y-10 md:grid-cols-4 md:gap-y-0 md:divide-x md:divide-white/10">
                    {s.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1, duration: 0.4 }}
                                className="flex flex-col items-center gap-2 text-center px-4"
                            >
                                <Icon className="h-7 w-7 text-[#C5A059] mb-2 opacity-90" strokeWidth={1.5} />
                                <span className="text-3xl font-bold text-white md:text-[40px] leading-none mb-1">
                                    <AnimatedCounter target={stat.value} suffix={stat.suffix} inView={isInView} />
                                </span>
                                <span className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-white/60">
                                    {stat.label}
                                </span>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default StatsStrip;
